/**
 * User id management.
 *
 * Persistent `kz_uid` cookie stores `userId:freq` (Yoto-compatible). When consent is
 * granted we resolve lifecycle: `user_init`, `user_existing`, or neither (returning
 * visitor on first tab hit). Session tab state uses sessionStorage `kz_usess`.
 */

import { getCookie, setCookie, deleteCookie } from '../../util/cookies';
import { random64BitHex } from './random';

const COOKIE_NAME = 'kz_uid';
const COOKIE_DAYS = 365;
const SESSION_TUPLE_KEY = 'kz_usess';

function safeSessionStorage(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

export interface UserIdContext {
  rootDomain: string;
}

export type UserLifecycle = 'init' | 'existing' | null;

export interface ParsedUidCookie {
  userId: string;
  freq: number;
  broken: boolean;
}

/** Parse `kz_uid` value: legacy plain hex or `userId:freq`. */
export function parseUidCookie(raw: string | null): ParsedUidCookie | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const idx = trimmed.lastIndexOf(':');
  if (idx <= 0) {
    if (/^[0-9a-f]{16}$/i.test(trimmed)) {
      return { userId: trimmed.toLowerCase(), freq: 1, broken: false };
    }
    return { userId: trimmed, freq: 1, broken: true };
  }
  const userId = trimmed.slice(0, idx);
  const freq = parseInt(trimmed.slice(idx + 1), 10);
  if (!userId || Number.isNaN(freq) || freq < 1) {
    return { userId, freq: 1, broken: true };
  }
  return { userId, freq, broken: false };
}

export function readPersistentUserId(): string | null {
  const p = parseUidCookie(getCookie(COOKIE_NAME));
  return p && !p.broken ? p.userId : null;
}

function setUserCookie(ctx: UserIdContext, userId: string, freq: number): void {
  setCookie(COOKIE_NAME, `${userId}:${freq}`, { days: COOKIE_DAYS, domain: ctx.rootDomain });
}

function writeSessionTuple(userId: string, freq: number): void {
  const store = safeSessionStorage();
  if (!store) return;
  try {
    store.setItem(SESSION_TUPLE_KEY, `${userId}:${freq}`);
  } catch {
    /* ignore */
  }
}

function readSessionTuple(): string | null {
  const store = safeSessionStorage();
  if (!store) return null;
  try {
    return store.getItem(SESSION_TUPLE_KEY);
  } catch {
    return null;
  }
}

/**
 * Yoto-parity persistent user resolution for a tab + cookie.
 * - `user_existing`: sessionStorage tuple already present (reload / re-init same tab).
 * - `user_init`: no cookie (or broken) → new id.
 * - `null`: returning visitor, first visit this tab (increment freq).
 */
export function resolvePersistentUser(ctx: UserIdContext): {
  userId: string;
  freq: number;
  userLifecycle: UserLifecycle;
} {
  const sessionVal = readSessionTuple();
  const cookieParsed = parseUidCookie(getCookie(COOKIE_NAME));

  const isBrokenTuple = (v: string | null) =>
    !!v && (v.startsWith('undefined:') || v.includes('undefined'));

  if (sessionVal && !isBrokenTuple(sessionVal)) {
    const p = parseUidCookie(sessionVal);
    if (p && !p.broken) {
      setUserCookie(ctx, p.userId, p.freq);
      writeSessionTuple(p.userId, p.freq);
      return { userId: p.userId, freq: p.freq, userLifecycle: 'existing' };
    }
  }

  if (!cookieParsed || cookieParsed.broken) {
    const userId = random64BitHex();
    const freq = 1;
    setUserCookie(ctx, userId, freq);
    writeSessionTuple(userId, freq);
    return { userId, freq, userLifecycle: 'init' };
  }

  const freq = cookieParsed.freq + 1;
  setUserCookie(ctx, cookieParsed.userId, freq);
  writeSessionTuple(cookieParsed.userId, freq);
  return { userId: cookieParsed.userId, freq, userLifecycle: null };
}

export function clearPersistentUserId(ctx: UserIdContext): void {
  deleteCookie(COOKIE_NAME, { domain: ctx.rootDomain });
  const store = safeSessionStorage();
  if (store) {
    try {
      store.removeItem(SESSION_TUPLE_KEY);
    } catch {
      /* ignore */
    }
  }
}

export function generateEphemeralUserId(): string {
  return random64BitHex();
}

export { COOKIE_NAME as KZ_UID_COOKIE_NAME, SESSION_TUPLE_KEY as KZ_USESS_KEY };
